import {
	Box3,
	DoubleSide,
	Group,
	Matrix4,
	Mesh,
	MeshBasicMaterial,
	SphereGeometry,
	TorusGeometry,
	Vector3
} from 'three';

import type { RotationGizmoManager } from '../rotation-gizmo-manager.class';

const isTopOnly = (subtype: string): boolean => {
	const normalized = subtype.toLowerCase().trim();
	return normalized === 'top only' || normalized === 'top';
};

export type CreateGizmoRingsParams = {
	targetObject: Group | Mesh;
};

/**
 * Creates ONE torus ring centered on the target appliance.
 *
 * Collada models have a built-in Rx(-PI/2) that remaps Euler axes:
 *   - rotation.z (last in Euler XYZ) operates in the Rx-rotated frame where
 *     local Z = world Y. So rotation.z spins around world Y = horizontal spin
 *     on the countertop. Used for top-only appliances.
 *   - rotation.y (second in Euler XYZ) operates in the Rx-rotated frame where
 *     local Y = world -Z. So rotation.y spins around world -Z = in the panel
 *     face plane. Used for panel appliances.
 *
 * Ring orientation:
 *   - top-only: green ring flat on countertop, axis = 'z'
 *   - panel: blue ring vertical parallel to panel face, axis = 'y'
 */
export const createGizmoRingsCfn = (self: RotationGizmoManager, params: CreateGizmoRingsParams): Group => {
	const { targetObject } = params;

	const bbox = new Box3().setFromObject(targetObject);
	const bboxSize = new Vector3();
	bbox.getSize(bboxSize);
	const bboxCenter = new Vector3();
	bbox.getCenter(bboxCenter);

	const rawSubtype = typeof targetObject.userData?.subtype === 'string'
		? targetObject.userData.subtype
		: typeof targetObject.userData?.placement === 'string'
			? targetObject.userData.placement
			: 'front only';
	const topOnly = isTopOnly(rawSubtype);

	// Ring center: countertop at base, panel at bbox center
	const ringCenter = topOnly
		? new Vector3(bboxCenter.x, bbox.min.y + 0.5, bboxCenter.z)
		: bboxCenter.clone();

	self.internalBboxCenter = ringCenter.clone();

	// Ring radius from relevant dimensions
	const ringRadius = topOnly
		? (Math.max(bboxSize.x, bboxSize.z) / 2) * 1.3
		: (Math.max(bboxSize.x, bboxSize.y) / 2) * 1.3;
	const tubeRadius = ringRadius * 0.03;

	// Euler axis mapping (after Collada Rx(-PI/2)):
	//   topOnly:  rotation.z = spin around world Y (horizontal on countertop)
	//   panel:    rotation.y = spin in panel face plane (around world -Z)
	const axis: 'y' | 'z' = topOnly ? 'z' : 'y';
	const color = topOnly ? 0x22cc22 : 0x2255ff;

	const gizmoGroup = new Group();
	gizmoGroup.userData = { type: 'rotation-gizmo-group' };
	gizmoGroup.position.copy(ringCenter);

	const geometry = new TorusGeometry(ringRadius, tubeRadius, 16, 64);
	const material = new MeshBasicMaterial({
		color,
		transparent: true,
		opacity: 0.5,
		side: DoubleSide,
		depthTest: false
	});

	const ring = new Mesh(geometry, material);
	ring.renderOrder = 999;
	ring.userData = { type: 'rotation-gizmo', axis };

	if (topOnly) {
		// Torus default lies flat in XZ, but the countertop is rendered as a
		// front panel rotated 90° back, so the visual countertop surface isn't
		// perfectly aligned with world XZ. Rotate the ring 90° around X to
		// match the visual countertop plane.
		ring.rotation.set(Math.PI / 2, 0, 0);
		// Ring normal = world Y (up). Drag plane is horizontal.
		self.internalRingNormal = new Vector3(0, 1, 0);
	} else {
		// Stand torus upright and align with the panel face.
		// gizmoGroup gets the appliance's world rotation so the ring
		// follows the panel orientation. Local PI/2 X rotation converts
		// from flat (XZ) to upright (XY).
		targetObject.updateWorldMatrix(true, false);
		const worldRot = new Matrix4().extractRotation(targetObject.matrixWorld);
		gizmoGroup.setRotationFromMatrix(worldRot);
		ring.rotation.set(Math.PI / 2, 0, 0);

		// Ring normal = panel facing direction for the drag plane.
		const panelAngle = targetObject.rotation.z;
		const ringNormal = new Vector3(Math.sin(panelAngle), 0, Math.cos(panelAngle));
		if (targetObject.parent) {
			targetObject.parent.updateWorldMatrix(true, false);
			const parentRot = new Matrix4().extractRotation(targetObject.parent.matrixWorld);
			ringNormal.applyMatrix4(parentRot).normalize();
		}
		self.internalRingNormal = ringNormal;
	}

	gizmoGroup.add(ring);

	// --- Debug shapes ---
	const debugMat = (c: number): MeshBasicMaterial => new MeshBasicMaterial({ color: c, depthTest: false });

	const centerSphere = new Mesh(new SphereGeometry(0.8, 12, 12), debugMat(0xffff00));
	centerSphere.renderOrder = 1000;
	centerSphere.userData = { type: 'rotation-gizmo' };
	gizmoGroup.add(centerSphere);

	const objWorldPos = new Vector3();
	targetObject.getWorldPosition(objWorldPos);
	const originSphere = new Mesh(new SphereGeometry(0.6, 12, 12), debugMat(0xff00ff));
	originSphere.position.copy(objWorldPos.sub(ringCenter));
	originSphere.renderOrder = 1000;
	originSphere.userData = { type: 'rotation-gizmo' };
	gizmoGroup.add(originSphere);

	self.internalGizmoGroup = gizmoGroup;
	self.internalGizmoRings = [ring];

	return gizmoGroup;
};
